// EAA-1667: The generic, safe localStorage primitives (SSR-/quota-safe read/write of
// JSON, strings, and key removal) moved to packages/hooks (storage). This file keeps
// only the web-content-specific keys, ranges, and validators layered on top of them.
import { readJson, writeJson, readString, writeString, removeKey } from '@ai-content-hub/hooks';

const STORAGE_PREFIX = 'web-content';

const PROGRAM_DETAIL_STEPS_KEY = `${STORAGE_PREFIX}:program-detail-steps`;
const PROGRAM_DETAIL_MAX_STEPS_KEY = `${STORAGE_PREFIX}:program-detail-max-steps`;
const LAST_TENANT_ID_KEY = `${STORAGE_PREFIX}:last-tenant-id`;
const CONTENT_BANK_TAG_TYPES_KEY = `${STORAGE_PREFIX}:cb-tag-types`;

const PROGRAM_DETAIL_MIN_STEP = 0;
const PROGRAM_DETAIL_MAX_STEP = 3;

function isIntegerRecord(value: unknown): value is Record<string, number> {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    return true;
}

/** Read a step map, keeping only integer values (drops any malformed entries). */
function readStepMap(key: string): Record<string, number> {
    const raw = readJson<Record<string, number>>(key, isIntegerRecord, {});
    const out: Record<string, number> = {};
    Object.entries(raw).forEach(([k, v]) => {
        if (typeof v === 'number' && Number.isInteger(v)) {
            out[k] = v;
        }
    });
    return out;
}

function isValidStep(step: number): boolean {
    return (
        Number.isInteger(step) && step >= PROGRAM_DETAIL_MIN_STEP && step <= PROGRAM_DETAIL_MAX_STEP
    );
}

function getStepFor(key: string, programId: string): number | null {
    if (!programId) {
        return null;
    }
    const step = readStepMap(key)[programId];
    return typeof step === 'number' && isValidStep(step) ? step : null;
}

function setStepFor(key: string, programId: string, step: number): void {
    if (!programId || !isValidStep(step)) {
        return;
    }
    const map = readStepMap(key);
    map[programId] = step;
    writeJson(key, map);
}

export function getProgramDetailStep(programId: string): number | null {
    return getStepFor(PROGRAM_DETAIL_STEPS_KEY, programId);
}

export function setProgramDetailStep(programId: string, step: number): void {
    setStepFor(PROGRAM_DETAIL_STEPS_KEY, programId, step);
}

export function getProgramDetailMaxStep(programId: string): number | null {
    return getStepFor(PROGRAM_DETAIL_MAX_STEPS_KEY, programId);
}

export function setProgramDetailMaxStep(programId: string, step: number): void {
    setStepFor(PROGRAM_DETAIL_MAX_STEPS_KEY, programId, step);
}

export function getLastSelectedTenantId(): string | null {
    return readString(LAST_TENANT_ID_KEY);
}

export function setLastSelectedTenantId(tenantId: string | null | undefined): void {
    if (!tenantId || tenantId.trim() === '') {
        removeKey(LAST_TENANT_ID_KEY);
        return;
    }
    writeString(LAST_TENANT_ID_KEY, tenantId);
}

// ── Content Bank tag type visibility (localStorage override per tenant) ──

function cbTagTypesStorageKey(tenantId: string): string {
    return `${CONTENT_BANK_TAG_TYPES_KEY}:${tenantId}`;
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value);
}

/**
 * Get locally-saved tag type IDs for a tenant.
 * Returns `null` when no local override exists (fall back to API `configured`).
 */
export function getContentBankTagTypeIds(tenantId: string): string[] | null {
    if (!tenantId) return null;
    const raw = readJson<string[] | null>(
        cbTagTypesStorageKey(tenantId),
        isNullableStringArray,
        null
    );
    if (!raw) return null;
    return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

function isNullableStringArray(value: unknown): value is string[] | null {
    return value === null || isStringArray(value);
}

/** Save tag type ID selection to localStorage for a tenant. */
export function setContentBankTagTypeIds(tenantId: string, tagTypeIds: string[]): void {
    if (!tenantId) return;
    writeJson(cbTagTypesStorageKey(tenantId), tagTypeIds);
}

/** Remove local override — reverts to API / default tag type config. */
export function clearContentBankTagTypeIds(tenantId: string): void {
    if (!tenantId) return;
    removeKey(cbTagTypesStorageKey(tenantId));
}
